import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';

const ListenPart4 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      // 1. Prioritize API data in review mode
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      // 2. Otherwise load from localStorage
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData && typeof savedData === 'object') {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach((qId) => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated]);


  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);

    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const sortedQuestions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);
  const exampleQuestion = sortedQuestions.find(q => q.points === 0);
  const mainQuestions = sortedQuestions.filter(q => q.points > 0);

  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

  const handleOptionSelect = (questionId, optionId) => {
    setAnswers(prevAnswers => ({
      ...prevAnswers,
      [questionId]: optionId,
    }));
  };

  const QuestionBlock = ({ question, questionNumber, showHint, isReviewMode, isHydrated }) => {
    const isExample = question.points === 0;
    const selectedOption = isExample ? (question.accepted_answers && question.accepted_answers[0]) || '' : answers[question.id];

    // Fix: is_correct is at top level
    const isCorrect = isReviewMode && (question?.is_correct === true || question?.is_correct === 'true');
    // Treat null/undefined as incorrect if in review mode
    const isIncorrect = isReviewMode && !isCorrect;
    const userSelectedAnswerId = question?.user_answer?.answer;
    const correctAnswerId = question.accepted_answers[0];

    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-3 md:p-4 shadow-sm flex flex-col justify-between hover:border-gray-300 transition-colors h-full">
        <p className="font-bold text-slate-800 dark:text-white mb-3 text-sm md:text-base leading-snug">
          {!isExample && <span className="font-black text-amber-600 dark:text-amber-400 mr-2 bg-amber-100 dark:bg-amber-900/50 px-2 py-0.5 rounded-lg text-sm border border-gray-200">{questionNumber}</span>}
          {isExample ? <span className="text-amber-600 mr-1">Ví dụ: </span> : ''}{question.question_text}
        </p>
        <div className="grid grid-cols-3 gap-2 flex-1">
          {question.options.map(option => {
            const isThisOptionCorrect = option.id === correctAnswerId;
            const isThisOptionUserSelected = option.id === userSelectedAnswerId;
            const isSelected = selectedOption === option.id;
            const isHintCorrect = showHint && isThisOptionCorrect;

            let cardClasses = `flex flex-col items-center gap-1.5 p-1.5 md:p-2 bg-white dark:bg-slate-800 rounded-xl shadow-sm border-2 transition-all cursor-pointer relative ${isSelected ? 'border-amber-400 bg-amber-50/80 dark:bg-amber-900/40 ring-2 ring-amber-200/50' : 'border-slate-100 dark:border-slate-700 hover:border-gray-200 hover:shadow-md'}`;
            if (isHintCorrect) {
              cardClasses = 'flex flex-col items-center gap-1.5 p-1.5 md:p-2 rounded-xl shadow-sm border-2 cursor-pointer relative border-amber-500 bg-amber-50 dark:bg-amber-900/20 ring-2 ring-amber-200';
            } else if (isReviewMode) {
              if (isThisOptionCorrect) {
                cardClasses = 'flex flex-col items-center gap-1.5 p-1.5 md:p-2 rounded-xl shadow-sm border-2 cursor-pointer relative border-green-500 bg-green-50/20 dark:bg-green-900/20 ring-2 ring-green-200';
              } else if (isThisOptionUserSelected && !isCorrect) {
                cardClasses = 'flex flex-col items-center gap-1.5 p-1.5 md:p-2 rounded-xl shadow-sm border-2 cursor-pointer relative border-red-500 bg-red-50/20 dark:bg-red-900/20 ring-2 ring-red-200';
              }
            }

            return (
              <div
                key={option.id}
                className={cardClasses}
                onClick={() => !isExample && !showHint && !isReviewMode && isHydrated && handleOptionSelect(question.id, option.id)}
              >
                <div className="w-full flex-1 min-h-0 flex justify-center items-center mb-1 bg-slate-50 dark:bg-slate-800/80 rounded-lg p-1.5 border border-slate-100 dark:border-slate-700 shadow-inner h-16 md:h-20 lg:h-24">
                  <img src={`${ASSET_BASE_URL}${option.image_url}`} alt={option.explain} className="max-h-full max-w-full object-contain mix-blend-multiply dark:mix-blend-normal" />
                </div>
                <div className="flex items-center gap-1 w-full justify-between px-1">
                  <span className={`font-display font-bold text-xs md:text-sm w-5 h-5 md:w-6 md:h-6 flex items-center justify-center rounded-full border ${isSelected || isHintCorrect || (isReviewMode && isThisOptionCorrect) ? 'text-amber-700 bg-amber-100 border-gray-300 dark:text-amber-300 dark:bg-amber-900/50 dark:border-amber-700' : 'text-slate-600 bg-slate-100 border-slate-200 dark:text-slate-300 dark:bg-slate-700 dark:border-slate-600'}`}>{option.id}</span>
                  <div className={`w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center transition-all 
                    ${isHintCorrect ? 'bg-amber-500 border-amber-500'
                      : isReviewMode
                        ? (isThisOptionCorrect ? 'bg-green-500 border-green-500' : (isThisOptionUserSelected && !isCorrect ? 'bg-red-500 border-red-500' : 'border-slate-300 dark:border-slate-600'))
                        : (isSelected ? 'bg-amber-500 border-amber-500' : 'border-slate-300 dark:border-slate-600')
                    }`}>
                    {(isHintCorrect || (isReviewMode && isThisOptionCorrect) || (!showHint && !isReviewMode && isSelected)) &&
                      <span className="text-white text-[10px] font-bold material-symbols-outlined leading-none">check</span>
                    }
                    {isReviewMode && isThisOptionUserSelected && !isCorrect &&
                      <span className="text-white text-[10px] font-bold material-symbols-outlined leading-none">close</span>
                    }
                  </div>
                </div>
                {isReviewMode && isThisOptionCorrect && <div className="absolute -top-1.5 -right-1.5 bg-white rounded-full leading-none shadow-sm"><span className="correct-checkmark !w-5 !h-5 !text-xs leading-none">✓</span></div>}
                {isReviewMode && isIncorrect && isThisOptionUserSelected && !isThisOptionCorrect && <div className="absolute -top-1.5 -right-1.5 bg-white rounded-full leading-none shadow-sm"><span className="incorrect-checkmark !w-5 !h-5 !text-xs leading-none">✗</span></div>}
              </div>
            )
          })}
        </div>
      </div>
    );
  };

  const allQuestions = exampleQuestion ? [exampleQuestion, ...mainQuestions] : mainQuestions;

  return (
    <div className="w-full h-full p-2 md:p-3">
      <div className="relative flex flex-col h-full bg-transparent dark:from-slate-800/50 dark:to-card-dark rounded-2xl p-3 md:p-4 overflow-y-auto custom-scrollbar">
        {/* Using m-auto here ensures vertical centering IF the content is shorter than the viewport, but prevents clipping top items if it's taller, unlike justify-center on the flex parent */}
        <div className="w-full m-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4 h-full">
            {allQuestions.map((q, index) => (
              <QuestionBlock
                key={q.id}
                question={q}
                questionNumber={q.points === 0 ? 0 : index}
                showHint={showHint}
                isReviewMode={isReviewMode}
                isHydrated={isHydrated}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListenPart4;